import React, { useState } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * TalentNetwork — public candidate-acquisition page at /talent-network.
 *
 * This is the A2P-compliant "organic web opt-in" surface. The intended
 * action is JOINING the talent network so a Banyak recruiter can match
 * the candidate to roles. That action completes with just a name and
 * email — a phone number and SMS consent are entirely optional add-ons.
 *
 * Compliance-critical behaviors (what a TCR reviewer will test):
 *   1. Submitting name + email (no phone, no SMS box) succeeds and shows
 *      the confirmation screen — proving the service is reachable without
 *      agreeing to SMS or providing a phone number.
 *   2. The SMS consent checkbox is unchecked by default and clearly
 *      labeled optional.
 *   3. The consent disclosure includes business name, message types,
 *      "recurring", frequency, "message and data rates may apply",
 *      STOP/HELP, and links to Terms + Privacy.
 *   4. Nothing about the phone/consent gates the Join button.
 *
 * The consent record itself is written server-side into the shared
 * sms_opt_ins ledger (only when the box is checked), so it flows through
 * the exact same send-gate as the existing public consent form.
 */

const CONSENT_TEXT = `I agree to receive recurring SMS text messages from Banyak Group LTD regarding recruitment outreach, job opportunities, interview scheduling, application status updates, and related candidate communications. Message frequency varies (typically 1–4 messages per active conversation). Message and data rates may apply. Reply STOP to any message to opt out, reply HELP for help. By checking this box I confirm I am the authorized user of the phone number provided and I agree to Banyak Group LTD's Terms of Service and Privacy Policy.`;

const TalentNetwork = () => {
  const [step, setStep] = useState('form'); // 'form' | 'done'

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [areaOfInterest, setAreaOfInterest] = useState('');
  const [location, setLocation] = useState('');
  const [message, setMessage] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [optedIntoSms, setOptedIntoSms] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate ONLY the service inputs. Phone/SMS never gate the join.
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!emailValid) { setError('Please enter a valid email address.'); return; }

    // If they ticked SMS but gave no number, nudge (don't block the idea
    // of joining — they can uncheck and still join).
    if (smsConsent && phone.trim().replace(/\D/g, '').length < 7) {
      setError('To receive SMS updates, add a mobile number above — or uncheck the SMS box to join without it.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/talent-network`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          area_of_interest: areaOfInterest.trim(),
          location: location.trim(),
          message: message.trim(),
          sms_consent: smsConsent,
          consent_text: smsConsent ? CONSENT_TEXT : null,
          consent_url: window.location.href,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not complete your signup. Please try again.');
      }
      setOptedIntoSms(smsConsent);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Could not complete your signup.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── DONE ──────────────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <Shell>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">You're in the network</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Thanks{name ? `, ${name.split(' ')[0]}` : ''}. We've added you to the Banyak Group talent network and a recruiter will reach out by email when a role fits your background.
          </p>
          {optedIntoSms ? (
            <p className="text-sm text-gray-600 leading-relaxed mt-3">
              You also opted in to SMS updates at {phone}. Reply <strong>STOP</strong> to any message to opt out at any time, or <strong>HELP</strong> for assistance.
            </p>
          ) : (
            <p className="text-xs text-gray-400 mt-4">
              You didn't opt in to SMS — that's completely fine. We'll stay in touch by email.
            </p>
          )}
          <p className="text-xs text-gray-400 mt-4">Questions? Contact oban@banyak.group.</p>
        </div>
      </Shell>
    );
  }

  // ─── FORM ──────────────────────────────────────────────────────────
  return (
    <Shell>
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
          <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-2a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-3-6.65" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Join the Banyak Group talent network</h1>
        <p className="text-sm text-gray-600 leading-relaxed">
          Tell us a little about yourself and our recruiters will reach out when an executive search matches your background. It's free and takes about a minute.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="tn-name" className="block text-xs font-medium text-gray-700 mb-1">
            Full name <span className="text-red-500">*</span>
          </label>
          <input id="tn-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="name" />
        </div>

        <div>
          <label htmlFor="tn-email" className="block text-xs font-medium text-gray-700 mb-1">
            Email address <span className="text-red-500">*</span>
          </label>
          <input id="tn-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="email" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="tn-area" className="block text-xs font-medium text-gray-700 mb-1">
              Area of interest <span className="text-gray-400">(optional)</span>
            </label>
            <input id="tn-area" type="text" value={areaOfInterest} onChange={(e) => setAreaOfInterest(e.target.value)}
              placeholder="e.g. Private Equity, CFO roles"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label htmlFor="tn-location" className="block text-xs font-medium text-gray-700 mb-1">
              Location <span className="text-gray-400">(optional)</span>
            </label>
            <input id="tn-location" type="text" value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. London, UK"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <div>
          <label htmlFor="tn-message" className="block text-xs font-medium text-gray-700 mb-1">
            Anything you'd like us to know? <span className="text-gray-400">(optional)</span>
          </label>
          <textarea id="tn-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="A sentence or two about the roles you're open to."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
        </div>

        {/* ── Optional SMS section ─────────────────────────────────────
            Visually set apart so it reads as a bonus add-on, not part of
            the required signup. Phone is optional; the checkbox is
            unchecked; neither gates the Join button. */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <p className="text-xs text-gray-600 leading-relaxed">
              <strong className="text-gray-800">Want faster updates by text?</strong> This is optional. You'll be added to the network and considered for roles whether or not you provide a mobile number or agree to SMS.
            </p>
          </div>

          <div>
            <label htmlFor="tn-phone" className="block text-xs font-medium text-gray-700 mb-1">
              Mobile phone number <span className="text-gray-400">(optional)</span>
            </label>
            <input id="tn-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="tel" />
          </div>

          <label htmlFor="tn-sms-consent" className="flex items-start gap-3 cursor-pointer">
            <input id="tn-sms-consent" type="checkbox" checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <span className="text-xs text-gray-700 leading-relaxed">
              <span className="text-gray-400 font-medium">(optional)</span>{' '}
              I agree to receive <strong>recurring SMS text messages</strong> from <strong>Banyak Group LTD</strong> regarding recruitment outreach, job opportunities, interview scheduling, application status updates, and related candidate communications. <strong>Message frequency varies</strong> (typically 1–4 messages per active conversation). <strong>Message and data rates may apply.</strong> Reply <strong>STOP</strong> to any message to opt out, reply <strong>HELP</strong> for help. By checking this box I confirm I am the authorized user of the phone number provided and I agree to Banyak Group LTD's{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a> and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>.
            </span>
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-3"><p className="text-xs text-red-700">{error}</p></div>
        )}

        <button type="submit" disabled={submitting}
          className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
          {submitting ? 'Joining…' : 'Join the talent network'}
        </button>

        <p className="text-[11px] text-gray-500 text-center leading-relaxed">
          Banyak Group LTD will not share your mobile information with third parties or affiliates for marketing purposes. SMS consent is not required to join or to be considered for any role.
        </p>
      </form>
    </Shell>
  );
};

// ─── Shared shell ─────────────────────────────────────
// Mirrors the SmsConsent page shell so the two public surfaces feel like
// one product to a reviewer clicking between them.
const Shell = ({ children }) => (
  <div className="min-h-screen bg-gray-50 px-4 py-10">
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Banyak Group LTD</h1>
        <p className="text-sm text-gray-500 mt-1">Talent Network</p>
      </div>
      <div className="bg-white rounded-lg shadow p-6 sm:p-8">
        {children}
      </div>
      <div className="mt-6 text-center">
        <p className="text-[11px] text-gray-400">
          <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> · <a href="/terms" className="text-blue-600 hover:underline">Terms</a>
        </p>
      </div>
    </div>
  </div>
);

export default TalentNetwork;
